import React, { useEffect } from "react";
import Header from "./Header";
import Footer from "./Footer";
import WhatsAppWidget from "./WhatsAppWidget";
import { Phone, Mail, Award, Users, Wifi, Smartphone, ArrowRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

const TrovaAgentePage = () => {
  const location = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]); // Ora si triggera ad ogni cambio di location/pathname

  const agents = [
    {
      name: "Giuseppe Lovecchio",
      title: "CEO & Fondatore",
      specialization: "Esperto in Connettività Dedicate e Centralini Virtuali",
      image: "https://customer-assets.emergentagent.com/job_site-redesign-7/artifacts/tuwd9a9l_GIUSEPPE%20LOVECCHIO.jpg",
      description: "Con oltre 20 anni di esperienza nelle telecomunicazioni, Giuseppe ha fondato Lovecchio Group con la visione di offrire soluzioni innovative per le aziende. È specializzato in connettività dedicate, centralini virtuali e architetture di rete complesse. La sua expertise abbraccia dalla progettazione di infrastrutture enterprise alla consulenza strategica per la digital transformation aziendale.",
      expertise: [
        "Connettività Fiber Dedicate",
        "Centralini Virtuali Enterprise", 
        "Architetture di Rete",
        "Consulenza Strategica ICT",
        "Digital Transformation"
      ],
      phone: "+39 333 1341439",
      email: "g.lovecchio@lovecchiogroup.it"
    },
    {
      name: "Alessandro Cannoletta",
      title: "Specialista Mobile & IoT",
      specialization: "Esperto in Soluzioni Mobili e Machine2Machine",
      image: "https://customer-assets.emergentagent.com/job_site-redesign-7/artifacts/9cg9hxqz_ALESSANDRO%20CANNOLETTA.jpg",
      description: "Alessandro è il nostro esperto di riferimento per tutte le soluzioni mobili e IoT. Con una profonda conoscenza delle tecnologie 5G, M2M e sistemi di monitoraggio remoto, aiuta le aziende a implementare soluzioni smart per l'industria 4.0. La sua passione per l'innovazione tecnologica lo rende il consulente ideale per progetti all'avanguardia.",
      expertise: [
        "Soluzioni Mobile Business",
        "Sistemi Machine2Machine",
        "Tecnologie 5G e IoT",
        "Monitoraggio Remoto",
        "Smart City Solutions"
      ],
      phone: "+39 328 6073595",
      email: "a.cannoletta@lovecchiogroup.it"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-900">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-black text-white py-20" style={{ paddingTop: '220px' }}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold mb-6">
              I Nostri <span className="text-orange-500">Esperti</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Un team di professionisti con oltre 20 anni di esperienza nelle telecomunicazioni, 
              pronti ad aiutarti a trovare la soluzione perfetta per il tuo business.
            </p>
          </div>
        </div>
      </section>

      {/* Agents Section */}
      <section className="bg-gray-900 py-20">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {agents.map((agent, index) => (
              <div 
                key={index}
                className="bg-gray-800 rounded-lg overflow-hidden hover:bg-gray-700 transition-all duration-300 transform hover:scale-105"
              >
                {/* Agent Photo */}
                <div className="relative overflow-hidden rounded-lg">
                  <div className="bg-black p-1 rounded-lg">
                    <img 
                      src={agent.image} 
                      alt={agent.name}
                      className="w-full h-72 object-cover object-top hover:object-contain hover:object-center transition-all duration-500 ease-in-out hover:scale-105 cursor-pointer rounded-lg"
                    />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/80 to-transparent p-6 rounded-b-lg">
                    <h3 className="text-2xl font-bold text-white mb-1">{agent.name}</h3>
                    <p className="text-orange-400 font-semibold">{agent.title}</p>
                  </div>
                </div>
                
                {/* Agent Info */}
                <div className="p-8">
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-orange-400 mb-3">
                      {agent.specialization}
                    </h4>
                    <p className="text-gray-300 leading-relaxed">
                      {agent.description}
                    </p>
                  </div>
                  
                  {/* Expertise */}
                  <div className="mb-6">
                    <h5 className="text-white font-semibold mb-3">Aree di Expertise:</h5>
                    <div className="grid grid-cols-1 gap-2">
                      {agent.expertise.map((skill, idx) => (
                        <div key={idx} className="flex items-center text-gray-300">
                          <div className="w-2 h-2 bg-orange-400 rounded-full mr-3"></div>
                          {skill}
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Contact Buttons */}
                  <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
                    <a 
                      href={`tel:${agent.phone}`}
                      className="flex items-center justify-center bg-orange-500 hover:bg-orange-600 text-black font-semibold px-4 py-3 rounded-lg transition-colors duration-200"
                    >
                      <Phone size={16} className="mr-2" />
                      Chiama
                    </a>
                    <a 
                      href={`mailto:${agent.email}?subject=Consulenza%20${agent.specialization}`}
                      className="flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-3 rounded-lg transition-colors duration-200"
                    >
                      <Mail size={16} className="mr-2" />
                      Email
                    </a>
                    <a 
                      href={`https://wa.me/39${agent.phone.replace('+39 ', '').replace(' ', '')}?text=Ciao%20${agent.name.split(' ')[0]}%2C%20vorrei%20una%20consulenza%20su%20${encodeURIComponent(agent.specialization)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center bg-green-600 hover:bg-green-700 text-white font-semibold px-4 py-3 rounded-lg transition-colors duration-200"
                    >
                      <Smartphone size={16} className="mr-2" />
                      WhatsApp
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Bottom CTA */}
          <div className="text-center mt-16">
            <h3 className="text-3xl font-bold text-white mb-8">
              Hai bisogno di una <span className="text-orange-500">consulenza personalizzata?</span>
            </h3>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              I nostri esperti sono a disposizione per analizzare le tue esigenze 
              e proporti la soluzione più adatta al tuo business.
            </p>
            <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              <Link 
                to="/preventivo"
                className="bg-orange-500 hover:bg-orange-600 text-black font-bold px-8 py-4 rounded-lg transition-colors duration-200 flex items-center justify-center"
              >
                Richiedi Preventivo Gratuito
                <ArrowRight size={20} className="ml-2" />
              </Link>
              <a 
                href="tel:+393331341439"
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-4 rounded-lg transition-colors duration-200 flex items-center justify-center"
              >
                <Phone size={20} className="mr-2" />
                Chiamaci Subito
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppWidget />
    </div>
  );
};

export default TrovaAgentePage;